import React, { useState, useEffect, useRef } from "react";

const RichTextEditor = ({ value, onChange }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [cursorPosition, setCursorPosition] = useState({ top: 0, left: 0 });
  const editorRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Function to fetch suggestions from the database
  const fetchSuggestions = async (term) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/article/suggest-articles?term=${term}`
      );
      const data = await response.json();
      setSuggestions(data);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSuggestions([]);
    }
  };

  // Handle input changes and trigger suggestions
  const handleInputChange = (e) => {
    const text = e.target.value;
    onChange(text);

    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = text.substring(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/(@|\$)[\w-]*$/);

    if (mentionMatch) {
      const searchTerm = mentionMatch[0].substring(1);
      setSearchTerm(searchTerm);

      // Calculate position for suggestions dropdown
      const textArea = editorRef.current;
      const cursorCoords = getCaretCoordinates(textArea, cursorPos);

      setCursorPosition({
        top: cursorCoords.top + textArea.offsetTop + 20,
        left: cursorCoords.left + textArea.offsetLeft,
      });

      if (searchTerm.length > 0) {
        fetchSuggestions(searchTerm);
        setShowSuggestions(true);
      }
    } else {
      setShowSuggestions(false);
    }
  };

  // Insert the selected suggestion
  const handleSuggestionClick = (suggestion) => {
    const text = value;
    const cursorPos = editorRef.current.selectionStart;
    const textBeforeCursor = text.substring(0, cursorPos);
    const textAfterCursor = text.substring(cursorPos);
    const mentionMatch = textBeforeCursor.match(/(@|\$)[\w-]*$/);

    if (mentionMatch) {
      const newText =
        textBeforeCursor.substring(0, mentionMatch.index) +
        `[${suggestion.title}](article/${suggestion.id})` +
        textAfterCursor;

      onChange(newText);
    }

    setShowSuggestions(false);
  };

  // Helper function to get caret coordinates
  const getCaretCoordinates = (element, position) => {
    const { lineHeight } = window.getComputedStyle(element);
    const lines = element.value.substr(0, position).split("\n");
    return {
      top: (lines.length - 1) * parseInt(lineHeight),
      left: lines[lines.length - 1].length * 8,
    };
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <textarea
        ref={editorRef}
        value={value}
        onChange={handleInputChange}
        className="w-full min-h-[200px] p-2 border rounded"
        placeholder="Type @ or $ to reference articles..."
      />

      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-10 bg-white border rounded shadow-lg max-h-48 overflow-y-auto"
          style={{
            top: `${cursorPosition.top}px`,
            left: `${cursorPosition.left}px`,
          }}
        >
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.id}
              className="p-2 hover:bg-blue-50 cursor-pointer"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              {suggestion.title}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// export default RichTextEditor;
export { RichTextEditor };
